import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, XCircle } from 'lucide-react';
import type { Permission } from '@garlic/shared';
import { api, errorMessage } from '../../lib/api';
import { formatBRL, formatDateTime, relativeTime } from '../../lib/format';
import { Alert, Button, Card, EmptyState, PageHeader, Spinner, cx } from '../../ui/primitives';
import { OrderStatusBadge } from '../../menu/OrderStatus';
import type { OrderView } from '../../menu/types';
import type { TenantRestaurant } from '../TenantApp';
import { CancelDialog, canCancelOrder, type CancelTarget } from './CancelDialog';

type StaffOrder = OrderView & { source: string; subtotalCents: number | null; session: { id: string; number: number; table: { label: string } } };

const FILTERS = [
  ['ACTIVE', 'Em andamento'],
  ['', 'Todos (24 h)'],
  ['CANCELLED', 'Cancelados'],
] as const;

/** Lista de pedidos das últimas 24 h. A operação por setor (cozinha/bar) chega na Fase 3. */
export function StaffOrders({ restaurant, permissions }: { restaurant: TenantRestaurant; permissions: Permission[] }) {
  const [status, setStatus] = useState<string>('ACTIVE');
  const [cancelling, setCancelling] = useState<CancelTarget | null>(null);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['staff-orders', status],
    queryFn: () => api<StaffOrder[]>(`/api/staff/orders${status ? `?status=${status}` : ''}`),
    refetchInterval: 10_000,
  });
  const canCancel = permissions.includes('orders.cancel_item');

  return (
    <>
      <PageHeader title="Pedidos" description="Atualiza automaticamente a cada 10 segundos." />
      <div className="mb-4 flex gap-1 rounded-xl bg-paper-2 p-1 text-sm sm:inline-flex">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={cx('flex-1 rounded-lg px-3 py-1.5 font-medium sm:flex-none', status === v ? 'bg-white shadow-sm' : 'text-ink-3')}>{l}</button>
        ))}
      </div>
      {q.isPending && <Spinner />}
      {q.isError && <Alert tone="danger">{errorMessage(q.error)}</Alert>}
      {q.data && q.data.length === 0 && <Card><EmptyState icon={<ClipboardList className="size-5" />} title="Nenhum pedido aqui" /></Card>}
      {q.data && q.data.length > 0 && (
        <ul className="grid gap-3 lg:grid-cols-2">
          {q.data.map((o) => (
            <li key={o.id}>
              <Card className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">
                      #{String(o.number).padStart(4, '0')} · Mesa {o.session.table.label}
                    </p>
                    <p className="text-xs text-ink-3">
                      {formatDateTime(o.createdAt, restaurant.timezone)} ({relativeTime(o.createdAt)}) · comanda {o.session.number}
                      {o.customerName ? ` · ${o.customerName}` : ''}{o.source === 'STAFF' ? ' · lançado pela equipe' : ''}
                    </p>
                  </div>
                  <OrderStatusBadge status={o.status} />
                </div>
                <ul className="mt-3 divide-y divide-line border-t border-line text-sm">
                  {o.items.map((i) => (
                    <li key={i.id} className="flex items-start gap-2 py-2">
                      <div className={cx('min-w-0 flex-1', i.status === 'CANCELLED' && 'text-ink-3 line-through')}>
                        <span className="font-medium">{i.quantity}×</span> {i.productName}
                        {i.options.length > 0 && <span className="block text-xs text-ink-3">{i.options.map((x) => `${x.groupName}: ${x.optionName}`).join(' · ')}</span>}
                        {i.notes && <span className="block text-xs font-medium text-warn-600">Obs.: {i.notes}</span>}
                      </div>
                      {i.unitPriceCents != null && <span className="tabular-nums text-ink-2">{formatBRL(i.unitPriceCents * i.quantity)}</span>}
                      {canCancel && i.status !== 'CANCELLED' && (
                        <button aria-label={`Cancelar ${i.productName}`} title="Cancelar item" onClick={() => setCancelling({ kind: 'item', id: i.id, label: `${i.quantity}× ${i.productName}` })} className="rounded p-0.5 text-ink-3 hover:text-danger-600">
                          <XCircle className="size-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {o.notes && <p className="mt-2 rounded-lg bg-warn-50 px-3 py-2 text-xs text-warn-600">Observação: {o.notes}</p>}
                {canCancel && canCancelOrder(o.status) && (
                  <Button variant="ghost" size="sm" className="mt-2 text-danger-600" icon={<XCircle className="size-4" />} onClick={() => setCancelling({ kind: 'order', id: o.id, label: `pedido #${String(o.number).padStart(4, '0')}` })}>
                    Cancelar pedido
                  </Button>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
      <CancelDialog target={cancelling} onClose={() => setCancelling(null)} onDone={() => qc.invalidateQueries({ queryKey: ['staff-orders'] })} />
    </>
  );
}
